import { initializeConflictsView } from '../actions/publicActions';
import getStore from '../store/store';
import { calendarCacheInitialized, initializeCalendarsCache } from 'owa-calendar-cache';
import { calendarEvent } from 'owa-calendar-event-converter';
import { expandCalendarEventService } from 'owa-calendar-events-store';
import { CalendarEvent } from 'owa-calendar-types';
import { ClientItemId } from 'owa-client-ids';
import { addYears, isBefore, today, userDate } from 'owa-datetime';
import { cleanTimeZoneId } from 'owa-datetime-store';
import { DayGridScenario, getCalendarFolderId, loadDayGrid, selectDayGridDate } from 'owa-day-grid';
import getRecurrenceEndDate from 'owa-recurrence-utils/lib/getRecurrenceEndDate';
import { getUserTimeZone } from 'owa-session-store';
import { orchestrator } from 'satcheljs';
import {
    navigateToPreviousInstance,
    navigateToNextInstance,
    setFutureInstances,
} from '../actions/timePanelConflictsViewActions';
import CalendarItem from 'owa-service/lib/contract/CalendarItem';

orchestrator(initializeConflictsView, async ({ meetingRequest }) => {
    const {
        Start,
        End,
        StartTimeZoneId,
        EndTimeZoneId,
        Recurrence,
        AssociatedCalendarItemId,
    } = meetingRequest;

    // Set future instances to the current event while recurrence expansion happens
    const event = {
        Start: userDate(Start),
        End: userDate(End),
        StartTimeZoneId: cleanTimeZoneId(StartTimeZoneId || getUserTimeZone()),
        EndTimeZoneId: cleanTimeZoneId(EndTimeZoneId || getUserTimeZone()),
    } as CalendarEvent;

    const store = getStore();

    setFutureInstances([event]);

    initializeCalendarsCache('TimePanelConflictsView');

    // Expand recurring items, but only if they have an Item Id (have not been canceled or deleted)
    if (Recurrence && AssociatedCalendarItemId) {
        const now = today();
        let recurrenceStart = userDate(Recurrence.RecurrenceRange.StartDate);
        if (isBefore(recurrenceStart, now)) {
            recurrenceStart = now;
        }
        const recurrenceEnd =
            getRecurrenceEndDate(StartTimeZoneId, Recurrence) || addYears(recurrenceStart, 1);
        const expandedOccurrences: CalendarItem[] = (await expandCalendarEventService(
            AssociatedCalendarItemId as ClientItemId,
            {
                start: recurrenceStart,
                end: recurrenceEnd,
            }
        )).Occurrences;

        // Check that info is still valid after async call
        if (
            !store.meetingRequest ||
            !store.meetingRequest.AssociatedCalendarItemId ||
            store.meetingRequest.AssociatedCalendarItemId.Id != AssociatedCalendarItemId.Id
        ) {
            return;
        }

        if (expandedOccurrences && expandedOccurrences.length > 0) {
            setFutureInstances(expandedOccurrences.map(item => calendarEvent(item, null)));
        }
    }
});

orchestrator(calendarCacheInitialized, ({ calendarCacheScenarioId }) => {
    if (calendarCacheScenarioId != 'TimePanelConflictsView') {
        return;
    }

    const store = getStore();
    if (!store.meetingRequest) {
        return;
    }

    const { meetingRequest } = store;
    const calendarFolderId = getCalendarFolderId(meetingRequest);
    const highlightId =
        store.meetingRequest.AssociatedCalendarItemId &&
        store.meetingRequest.AssociatedCalendarItemId.Id;

    loadDayGrid(
        DayGridScenario.TimePanelConflictsView,
        userDate(meetingRequest.Start),
        { start: userDate(meetingRequest.Start), end: userDate(meetingRequest.End) },
        calendarFolderId ? [calendarFolderId] : null,
        highlightId
    );
});

orchestrator(navigateToPreviousInstance, () => {
    const { futureInstances, selectedInstanceIndex } = getStore();
    const startDate = futureInstances[selectedInstanceIndex].Start;
    selectDayGridDate(DayGridScenario.TimePanelConflictsView, startDate);
});

orchestrator(navigateToNextInstance, () => {
    const { futureInstances, selectedInstanceIndex } = getStore();
    const startDate = futureInstances[selectedInstanceIndex].Start;
    selectDayGridDate(DayGridScenario.TimePanelConflictsView, startDate);
});
